import {
  Mail,
  Home, ClipboardList, Play, FileText, Activity, Workflow, MonitorSmartphone, Presentation, Smartphone, NotebookPen, NotebookText,
  Settings2, Shapes, Users, Database, Download, KeyRound, UserCog,
  ScrollText, BarChart3, Layers, Book, BookOpen, MessageSquare, FlaskConical, Tv,
} from 'lucide-react';
import { hasRole } from '../../utils/roles';
import { canOnInstance } from '../../utils/access';

// Admin navigation, in two scopes (specs/admin-instance-lifecycle.md).
// Instance pages work on the selected instance; their links carry ?instance=.
// They are grouped into stages that follow the workshop: Valmistelu → Toteutus
// → Tulokset, plus Seuranta. The stage row under the top bar shows the stage
// selector and the active stage's tabs.
// SERVER_NAV pages are server-wide ("Ylläpito"); their links carry no instance.
// A link is active when location.pathname equals one of its activePaths.
//
// Two ways in, either is enough (specs/access-model.md):
//   minRole        — the least system role (utils/roles.js), set on a stage
//                    for all its items and overridden per item.
//   instanceAction — the grant on the SELECTED instance that opens the page:
//                    Toteutus, Raportti and the Valmistelu pages a facilitator
//                    works on (Kulku, Käsikirjoitus, Sisältö, Näyttösisältö,
//                    Tietopohja; #1214) take facilitate, the rest manage.
//                    So a Kuutio's own facilitator runs it whatever their role.
//                    Seuranta has none: the logs span every instance (#933).
//                    An item sets `instanceAction: null` to take none, so only
//                    the role opens it (Valmistelu › Promptit, #705).
// SERVER_NAV items may instead name `grants`: a resource type whose grant
// opens the page (Aineistot for dataset grant holders).
// AuthGate reads all of this through mayOpenPath, so the nav and the gate
// cannot disagree.

export const INSTANCE_HOME = {
  key: 'home', label: 'Yleiskuva', icon: Home, path: '/admin', activePaths: ['/admin'], minRole: 'admin', instanceAction: 'manage',
};

export const INSTANCE_STAGES = [
  {
    key: 'prepare',
    minRole: 'admin',
    instanceAction: 'manage',
    label: 'Valmistelu',
    icon: ClipboardList,
    items: [
      { key: 'basics', label: 'Perustiedot', icon: Settings2, path: '/admin/settings', activePaths: ['/admin/settings'] },
      { key: 'flow', label: 'Kulku', icon: Workflow, path: '/admin/flow', activePaths: ['/admin/flow'], instanceAction: 'facilitate' },
      // The facilitation script of the chain, reviewed and customized before the Kuutio (#1082)
      { key: 'script', label: 'Käsikirjoitus', icon: NotebookPen, path: '/admin/script', activePaths: ['/admin/script'], instanceAction: 'facilitate' },
      { key: 'content', label: 'Sisältö', icon: Shapes, path: '/admin/content', activePaths: ['/admin/content'], instanceAction: 'facilitate' },
      { key: 'screen-content', label: 'Näyttösisältö', icon: Presentation, path: '/admin/screen-content', activePaths: ['/admin/screen-content'], instanceAction: 'facilitate' },
      { key: 'participants', label: 'Osallistujat', icon: Users, path: '/admin/participants', activePaths: ['/admin/participants'] },
      { key: 'access', label: 'Käyttöoikeudet', icon: KeyRound, path: '/admin/access', activePaths: ['/admin/access'] },
      { key: 'viestit', label: 'Viestit', icon: Mail, path: '/admin/viestit', activePaths: ['/admin/viestit'] },
      // Facilitators set the analysis depth here; choosing datasets stays manage (DatasetsScreen)
      { key: 'datasets', label: 'Tietopohja', icon: Database, path: '/admin/datasets', activePaths: ['/admin/datasets'], instanceAction: 'facilitate' },
      // This Kuutio's own versions of the AI prompts: admins only, like Ylläpito › Promptit (#705)
      { key: 'instance-prompts', label: 'Promptit', icon: MessageSquare, path: '/admin/instance-prompts', activePaths: ['/admin/instance-prompts'], instanceAction: null },
    ],
  },
  {
    key: 'run',
    minRole: 'facilitator',
    instanceAction: 'facilitate',
    label: 'Toteutus',
    icon: Play,
    items: [
      // /admin/play (Esitysnäkymä, linked from Työpaja's Näytöt) opens with it
      { key: 'workshop', label: 'Työpaja', icon: Play, path: '/admin/workshop', activePaths: ['/admin/workshop', '/admin/play'] },
      // The same script during the Kuutio, for its facilitators (#1082); prints for the room
      { key: 'run-script', label: 'Käsikirjoitus', icon: NotebookPen, path: '/admin/workshop/script', activePaths: ['/admin/workshop/script'] },
      // The workshop in the facilitator's hand: a phone page, no header of its own
      { key: 'click', label: 'Kaukosäädin', icon: Smartphone, path: '/admin/click', activePaths: ['/admin/click'] },
      { key: 'devices', label: 'Laitteet', icon: MonitorSmartphone, path: '/screen-manager', activePaths: ['/screen-manager', '/admin/screen-manager'] },
    ],
  },
  {
    key: 'results',
    minRole: 'admin',
    instanceAction: 'manage',
    label: 'Tulokset',
    icon: FileText,
    items: [
      // The Kuutio's facilitator creates and translates its report too
      { key: 'report', label: 'Raportti', icon: FileText, path: '/reportgen', activePaths: ['/reportgen'], minRole: 'facilitator', instanceAction: 'facilitate' },
      { key: 'export', label: 'Vienti', icon: Download, path: '/admin/export', activePaths: ['/admin/export'] },
    ],
  },
  {
    key: 'monitor',
    minRole: 'admin',
    label: 'Seuranta',
    icon: Activity,
    items: [
      { key: 'log', label: 'Loki', icon: ScrollText, path: '/admin/activity-log', activePaths: ['/admin/activity-log'] },
      { key: 'ai-usage', label: 'AI-käyttö', icon: BarChart3, path: '/admin/ai-usage', activePaths: ['/admin/ai-usage'] },
    ],
  },
];

export const SERVER_NAV = [
  { key: 'instances', label: 'Instanssit', icon: Layers, path: '/admin/instances', activePaths: ['/admin/instances'], minRole: 'admin' },
  // Aineistot also opens to anyone holding a dataset grant (specs/access-model.md):
  // the backend filters the list and gates each dataset by the grant.
  { key: 'knowledge', label: 'Aineistot', icon: Book, path: '/admin/knowledge', activePaths: ['/admin/knowledge'], minRole: 'admin', grants: 'dataset' },
  { key: 'users', label: 'Käyttäjät', icon: UserCog, path: '/admin/users', activePaths: ['/admin/users'], minRole: 'usermanager' },
  // The modules' system-level facilitation texts, versioned like the prompts (#1091)
  { key: 'facilitation', label: 'Fasilitointi', icon: NotebookText, path: '/admin/facilitation', activePaths: ['/admin/facilitation'], minRole: 'admin' },
  // The print booklet's designer PDFs, one per slot and language (#1329)
  { key: 'booklet', label: 'Vihko', icon: BookOpen, path: '/admin/booklet', activePaths: ['/admin/booklet'], minRole: 'admin' },
  { key: 'prompts', label: 'Promptit', icon: MessageSquare, path: '/admin/prompts', activePaths: ['/admin/prompts'], minRole: 'admin' },
  { key: 'qa', label: 'QA', icon: FlaskConical, path: '/admin/qa', activePaths: ['/admin/qa'], minRole: 'admin' },
  { key: 'info', label: 'Infoseinä', icon: Tv, path: '/admin/info', activePaths: ['/admin/info'], minRole: 'admin' },
];

/** Where a facilitator lands when a page needs more than their role. */
export const FACILITATOR_HOME = '/admin/workshop';

export const isNavItemActive = (item, pathname) => item.activePaths.includes(pathname);

/** 'server' on a server-wide page, otherwise 'instance'. */
export const getNavScope = (pathname) =>
  (SERVER_NAV.some(item => isNavItemActive(item, pathname)) ? 'server' : 'instance');

/** The stage whose tabs include this path, or null (e.g. on Yleiskuva). */
export const getStageForPath = (pathname) =>
  INSTANCE_STAGES.find(stage => stage.items.some(item => isNavItemActive(item, pathname))) || null;

/** An item's grant: its own, `null` for none, else its stage's. */
const itemAction = (item, stage) => ('instanceAction' in item ? item.instanceAction : stage.instanceAction);

// path → { role, grants, instanceAction }, built once from the config above.
const PATH_ACCESS = new Map([
  ...INSTANCE_HOME.activePaths.map(path => [path, { role: INSTANCE_HOME.minRole, instanceAction: INSTANCE_HOME.instanceAction }]),
  ...INSTANCE_STAGES.flatMap(stage => stage.items.flatMap(item =>
    item.activePaths.map(path => [path, {
      role: item.minRole || stage.minRole,
      instanceAction: itemAction(item, stage),
    }]))),
  ...SERVER_NAV.flatMap(item => item.activePaths.map(path => [path, { role: item.minRole, grants: item.grants }])),
]);

/**
 * The least role that may open this path: from the nav config, else 'admin'
 * for anything under /admin, else null (not an admin page).
 */
export const getRequiredRole = (pathname) => {
  if (PATH_ACCESS.has(pathname)) return PATH_ACCESS.get(pathname).role;
  return pathname === '/admin' || pathname.startsWith('/admin/') ? 'admin' : null;
};

/** Any grant of this resource type (served by /auth/me as `grants`)? */
const hasAnyGrant = (user, resourceType) =>
  !!resourceType && Object.keys(user?.grants?.[resourceType] || {}).length > 0;

/** May this user open this nav entry (an item, a server item or INSTANCE_HOME)? */
export const canOpen = (user, entry, instanceId = null) =>
  hasRole(user, entry.minRole)
  || hasAnyGrant(user, entry.grants)
  || canOnInstance(user, instanceId, entry.instanceAction);

/**
 * Can a grant open this path, not only a role? Such a page may be meant for
 * someone outside Sitra (a dataset's contributor, #799), so AuthGate sends a
 * visitor without a session to /login, which offers an email code beside
 * Microsoft, instead of straight to Sitra's Entra.
 */
export const grantOpensPath = (pathname) => {
  const access = PATH_ACCESS.get(pathname);
  return !!(access?.grants || access?.instanceAction);
};

/**
 * The AuthGate check for an admin path: the role, a grant the path honours,
 * or the required action on the selected instance.
 */
export const mayOpenPath = (user, pathname, instanceId = null) => {
  const access = PATH_ACCESS.get(pathname);
  if (!access) return hasRole(user, getRequiredRole(pathname));
  return hasRole(user, access.role)
    || hasAnyGrant(user, access.grants)
    || canOnInstance(user, instanceId, access.instanceAction);
};

/**
 * The stages this user may open for this instance, each with only the items
 * they may open (an item without minRole / instanceAction takes its stage's).
 * A stage with none is left out.
 */
export const stagesFor = (user, instanceId = null) => INSTANCE_STAGES
  .map((stage) => ({
    ...stage,
    items: stage.items.filter((item) => canOpen(user, {
      minRole: item.minRole || stage.minRole,
      instanceAction: itemAction(item, stage),
    }, instanceId)),
  }))
  .filter((stage) => stage.items.length > 0);
